import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../services/authService';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';

const CreateJourneyPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Accounts state
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState('');

  // Campaigns state
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState('');

  // Funnels state
  const [funnels, setFunnels] = useState([]);
  const [funnelsLoading, setFunnelsLoading] = useState(false);
  const [selectedFunnel, setSelectedFunnel] = useState('');

  const [form, setForm] = useState({
    name: '',
    description: '',
    start_date: null,
    end_date: null,
  });

  // Fetch accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      setAccountsLoading(true);
      try {
        const response = await api.get('/crm/accounts/');
        setAccounts(response.data);
      } catch (error) {
        console.error('Error fetching accounts:', error);
        setError('Failed to load accounts');
      } finally {
        setAccountsLoading(false);
      }
    };
    fetchAccounts();
  }, []);

  // Validate selectedAccount
  useEffect(() => {
    if (!accountsLoading && selectedAccount) {
      const accountExists = accounts.some((a) => String(a.id) === String(selectedAccount));
      if (!accountExists) {
        setSelectedAccount('');
      }
    }
  }, [accountsLoading, accounts, selectedAccount]);

  // Fetch campaigns
  useEffect(() => {
    const fetchCampaigns = async () => {
      if (selectedAccount) {
        setCampaignsLoading(true);
        try {
          const response = await api.get(`/crm/campaigns/?account_id=${selectedAccount}`);
          setCampaigns(response.data);
        } catch (error) {
          console.error('Error fetching campaigns:', error);
          setError('Failed to load campaigns');
        } finally {
          setCampaignsLoading(false);
        }
      } else {
        setCampaigns([]);
      }
    };
    fetchCampaigns();
  }, [selectedAccount]);

  // Validate selectedCampaign
  useEffect(() => {
    if (!campaignsLoading && selectedCampaign) {
      if (campaigns.length > 0) {
        const campaignExists = campaigns.some((c) => String(c.id) === String(selectedCampaign));
        if (!campaignExists) {
          setSelectedCampaign('');
        }
      } else {
        setSelectedCampaign('');
      }
    }
  }, [campaignsLoading, campaigns, selectedCampaign]);

  // Fetch funnels
  useEffect(() => {
    const fetchFunnels = async () => {
      if (selectedCampaign) {
        setFunnelsLoading(true);
        try {
          const response = await api.get(`/crm/funnels/?campaign_id=${selectedCampaign}`);
          setFunnels(response.data);
        } catch (error) {
          console.error('Error fetching funnels:', error);
          setError('Failed to load funnels');
        } finally {
          setFunnelsLoading(false);
        }
      } else {
        setFunnels([]);
      }
    };
    fetchFunnels();
  }, [selectedCampaign]);

  // Validate selectedFunnel
  useEffect(() => {
    if (!funnelsLoading && selectedFunnel) {
      if (funnels.length > 0) {
        const funnelExists = funnels.some((f) => String(f.id) === String(selectedFunnel));
        if (!funnelExists) {
          setSelectedFunnel('');
        }
      } else {
        setSelectedFunnel('');
      }
    }
  }, [funnelsLoading, funnels, selectedFunnel]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (name) => (date) => {
    setForm(prev => ({ ...prev, [name]: date }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAccount || !selectedCampaign || !selectedFunnel) {
      setError('Please select an account, campaign, and funnel');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/acs/journeys/', {
        ...form,
        account: selectedAccount,
        campaign: selectedCampaign,
        funnel: selectedFunnel,
        start_date: form.start_date?.toISOString(),
        end_date: form.end_date?.toISOString(),
      });
      navigate(`/crm/journeys/${response.data.id}/builder`);
    } catch (err) {
      console.error('Error creating journey:', err);
      setError(err.response?.data?.error || 'Failed to create journey');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={3}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Create New Journey
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Journey Name"
                name="name"
                value={form.name}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                name="description"
                value={form.description}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Account</InputLabel>
                <Select
                  value={selectedAccount}
                  onChange={(e) => setSelectedAccount(e.target.value)}
                  label="Account"
                  disabled={accountsLoading}
                  required
                >
                  {accounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Campaign</InputLabel>
                <Select
                  value={selectedCampaign}
                  onChange={(e) => setSelectedCampaign(e.target.value)}
                  label="Campaign"
                  disabled={campaignsLoading || !selectedAccount}
                  required
                >
                  {campaigns.map((campaign) => (
                    <MenuItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Funnel</InputLabel>
                <Select
                  value={selectedFunnel}
                  onChange={(e) => setSelectedFunnel(e.target.value)}
                  label="Funnel"
                  disabled={funnelsLoading || !selectedCampaign}
                  required
                >
                  {funnels.map((funnel) => (
                    <MenuItem key={funnel.id} value={funnel.id}>
                      {funnel.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  label="Start Date"
                  value={form.start_date}
                  onChange={handleDateChange('start_date')}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  label="End Date"
                  value={form.end_date}
                  onChange={handleDateChange('end_date')}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => navigate('/crm/journeys')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading || !selectedAccount || !selectedCampaign || !selectedFunnel}
                >
                  {loading ? <CircularProgress size={24} /> : 'Create Journey'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default CreateJourneyPage; 